import type {
  ChatConversationMember,
  ChatRenderContext,
} from "@/components/shared/chat-message-types";
import { getFirstName } from "@/lib/utils/get-first-name";
import {
  isMenteeIdentity,
  isMentorIdentity,
} from "@/lib/utils/system-roles";
import { useMemo } from "react";

export interface ConversationParticipant {
  id: string | null;
  name: string | null;
  firstName: string | null;
  avatarUrl: string | null;
}

const EMPTY: ConversationParticipant = {
  id: null,
  name: null,
  firstName: null,
  avatarUrl: null,
};

export function useConversationParticipants(context: ChatRenderContext | undefined) {
  return useMemo(() => {
    const members = context?.conversation?.members ?? [];
    return {
      mentor: pickByIdentity(members, isMentorIdentity),
      teen: pickByIdentity(members, isMenteeIdentity),
    };
  }, [context]);
}

export function findParticipantById(
  context: ChatRenderContext | undefined,
  userId: string | null | undefined,
): ConversationParticipant {
  if (!userId) return EMPTY;
  const members = context?.conversation?.members ?? [];
  return toParticipant(members.find((m) => m.id === userId));
}

function pickByIdentity(
  members: ChatConversationMember[],
  matchesIdentity: (systemRoles: string[] | undefined) => boolean,
): ConversationParticipant {
  return toParticipant(members.find((m) => matchesIdentity(m.systemRoles)));
}

function toParticipant(
  member: ChatConversationMember | undefined,
): ConversationParticipant {
  if (!member) return EMPTY;
  return {
    id: member.id,
    name: member.name ?? null,
    firstName: getFirstName(member.name),
    avatarUrl: member.avatarUrl ?? null,
  };
}
